/**
 * CardLayer — title cards drawn INTO the canvas, so they end up in the recording.
 *
 * The obvious way to put text over a game is a DOM element, and it is wrong here for one
 * reason that is easy to miss until the file comes back: `canvas.captureStream()` records the
 * CANVAS, not the page. An HTML card sits on screen looking perfect, is visible in every
 * screenshot, and simply is not in the video. The only way a card gets recorded is if the
 * renderer draws it.
 *
 * So: text to an offscreen 2D canvas, that canvas as a texture, and one full-screen quad drawn
 * after the scene with the depth test off. Costs one draw call on the frames that have a card.
 *
 * Tracking is applied by hand, a glyph at a time. `ctx.letterSpacing` exists in current Chrome
 * and nowhere else, and this is the one piece of type in the project whose spacing has to match
 * the landing page exactly — a title card that is visibly not the game's own lettering reads as
 * a placeholder.
 */
import * as THREE from 'three';

const FONT_STACK = 'Bahnschrift, "DIN Condensed", "DIN Alternate", Oswald, ' +
  '"Roboto Condensed", "Avenir Next Condensed", "Arial Narrow", sans-serif';

/** seconds a card takes to arrive and to leave; slow, to match the cutting */
const FADE = 1.1;

/** where a card's copy breaks. Long lines are set over two rather than shrunk to fit one. */
export const LINE_BREAK = '\n';

export class CardLayer {
  private readonly surface: HTMLCanvasElement;
  private readonly ctx2d: CanvasRenderingContext2D | null;
  private readonly texture: THREE.CanvasTexture;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly mesh: THREE.Mesh;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  private alpha = 0;
  private target = 0;
  private drawn = '';

  constructor(width: number, height: number) {
    this.surface = document.createElement('canvas');
    this.surface.width = width;
    this.surface.height = height;
    this.ctx2d = this.surface.getContext('2d');

    this.texture = new THREE.CanvasTexture(this.surface);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    // The scene is tone-mapped; the card is not part of the scene and must not be graded.
    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      opacity: 0,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  /** '' clears the card. Safe to call every frame with the same value. */
  show(title: string, sub: string): void {
    const key = `${title} ${sub}`;
    this.target = title === '' ? 0 : 1;
    if (title === '' || key === this.drawn) return;
    this.drawn = key;
    this.paint(title, sub);
  }

  /** @returns true if there is anything to draw this frame */
  update(dt: number): boolean {
    const k = Math.min(1, dt / FADE);
    this.alpha += (this.target - this.alpha) * k;
    if (this.alpha < 0.002) this.alpha = 0;
    this.material.opacity = this.alpha;
    return this.alpha > 0;
  }

  dispose(): void {
    this.texture.dispose();
    this.material.dispose();
    this.mesh.geometry.dispose();
  }

  private paint(title: string, sub: string): void {
    const c = this.ctx2d;
    if (!c) return;
    const w = this.surface.width;
    const h = this.surface.height;
    c.clearRect(0, 0, w, h);

    // Sized off the SHORT edge, so a card reads the same in 9:16 and 1:1.
    const unit = Math.min(w, h);
    const subSize = Math.round(unit * 0.0165);
    const lines = title.split(LINE_BREAK);

    /* AUTO-FIT, and it is not a nicety. A card is set in caps at 0.24em of tracking, which is
       roughly 0.8 of the font size per character — so a line of thirty characters wants 1700px
       in a frame that is 1080 wide. Without this the copy simply runs off both edges of the
       master, and the only place it would be noticed is in the finished file. */
    const limit = w * 0.84;
    let titleSize = Math.round(unit * 0.062);
    for (let guard = 0; guard < 12; guard++) {
      c.font = `${titleSize}px ${FONT_STACK}`;
      let widest = 0;
      for (const line of lines) widest = Math.max(widest, this.measure(c, line, titleSize * 0.24));
      if (widest <= limit || titleSize <= unit * 0.022) break;
      titleSize = Math.round(titleSize * Math.min(0.92, limit / widest));
    }

    const lead = titleSize * 1.28;
    const block = lead * (lines.length - 1);
    const cy = Math.round(h * 0.5 - block / 2);

    c.textBaseline = 'middle';
    c.shadowColor = 'rgba(0,0,0,0.85)';
    c.shadowBlur = Math.round(unit * 0.03);

    c.fillStyle = '#E8E2D0';
    c.font = `${titleSize}px ${FONT_STACK}`;
    lines.forEach((line, i) => {
      this.tracked(c, line, w / 2, cy + i * lead, titleSize * 0.24);
    });

    if (sub !== '') {
      c.fillStyle = 'rgba(232,226,208,0.60)';
      c.font = `${subSize}px ${FONT_STACK}`;
      this.tracked(c, sub, w / 2, cy + block + titleSize * 0.95, subSize * 0.30);
    }

    c.shadowBlur = 0;
    this.texture.needsUpdate = true;
  }

  /** width of a tracked line: the advances, plus n-1 gaps between them */
  private measure(c: CanvasRenderingContext2D, text: string, gap: number): number {
    const chars = [...text];
    let total = 0;
    for (const ch of chars) total += c.measureText(ch).width;
    return total + gap * Math.max(0, chars.length - 1);
  }

  /**
   * Draw centred text with per-glyph tracking.
   *
   * The width is measured as the sum of the advances PLUS the gaps between them — n-1 gaps, not
   * n. Including the trailing gap is the same half-a-space error that had the landing page's
   * title sitting off-centre, and on a full-screen card it would be far more obvious.
   */
  private tracked(
    c: CanvasRenderingContext2D, text: string, cx: number, y: number, gap: number,
  ): void {
    const chars = [...text];
    let x = cx - this.measure(c, text, gap) / 2;
    for (const ch of chars) {
      c.fillText(ch, x, y);
      x += c.measureText(ch).width + gap;
    }
  }
}
